import { ViewBooking } from "collins-js-sdk";
import { User } from "lucide-react";
import React, { useState } from "react";
import { addOrUpdateBooking } from "../../../../../APIs/Booking/UpdateBooking";
import { useBookingPermissions } from "../../../../../hooks/useBookingPermissions";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { useNotificationsContext } from "../../../../../hooks/useNotificationsContext";
import { useUserContext } from "../../../../../hooks/useUserContext";
import { VenueConfig } from "../../../../../types/venues";
import {
  formatTimeForLocale,
  getGuestCount,
  isBookingValid,
  isNonWalkInBookingValid
} from "../../../../../utils/booking";
import {
  getBookingDurationAsString,
  getBookingEndTimeAsString,
  getBookingTimeAsString,
  getBookingType
} from "../../../../../utils/singleBooking";
import { EDSBadge } from "../../../../eds/EDSBadge";
import { Button } from "../../../../Shared/Buttons/Button";
import DetachedModal from "../../../../Shared/Modals/DetachedModal/DetachedModal";
import EditBookingCustomer from "./EditBookingCustomer";
import EditBookingDetails from "./EditBookingDetails";

type EditBookingType = {
  handleClose: () => void;
  booking?: ViewBooking;
  venueConfig?: VenueConfig;
};

const EditBooking = ({
  handleClose,
  booking: propBooking,
  venueConfig: propVenueConfig
}: EditBookingType) => {
  const bookingContext = useDiaryContext();
  const notificationsContext = useNotificationsContext();
  const { user } = useUserContext();
  const { userCanSaveBooking, userCanManageBooking } = useBookingPermissions();

  const { selectedBooking, venueConfig: contextVenueConfig } = bookingContext;
  const effectiveBooking = propBooking || selectedBooking;
  const effectiveVenueConfig = propVenueConfig || contextVenueConfig;
  const [booking, setBooking] = useState<ViewBooking>(effectiveBooking ?? {});

  // Update booking state when effectiveBooking changes
  React.useEffect(() => {
    if (effectiveBooking) {
      setBooking(effectiveBooking);
    }
  }, [effectiveBooking]);

  if (!effectiveBooking || !effectiveVenueConfig) {
    return null;
  }

  const guestCount = getGuestCount([effectiveBooking]);
  const bookingTimeAsString = getBookingTimeAsString(effectiveBooking);
  const bookingEndTimeAsString = getBookingEndTimeAsString(effectiveBooking);
  const bookingDurationAsString = getBookingDurationAsString(effectiveBooking);
  const bookingType = getBookingType({
    selectedBooking: effectiveBooking,
    venueConfig: effectiveVenueConfig
  });

  const updateBooking = (bookingProps: Partial<ViewBooking>) => {
    setBooking({ ...booking, ...bookingProps });
  };

  let saveDisabled = isBookingValid(booking) || !userCanSaveBooking;
  if (!booking.walkIn || booking.waitlisted) {
    saveDisabled = isNonWalkInBookingValid(booking) || !userCanSaveBooking;
  }

  const saveBooking = async () => {
    handleClose();

    const response = await addOrUpdateBooking({
      booking,
      diaryContext: bookingContext
    });

    const isEditingFromPreviewModal = bookingContext.editingFromPreviewModal;

    if (isEditingFromPreviewModal) {
      bookingContext.setDiaryContext({
        editingFromPreviewModal: false,
        selectedView: "RunsheetTab",
        selectedBooking: undefined,
        selectedBookingV4: undefined
      });
    }

    notificationsContext.addNotification({
      heading:
        !response || response?.unsaved ? "Update pending" : "Booking updated",
      autoClose: 4000,
      toastType: "information"
    });
  };

  const editBookingContent = (
    <div className="EditBooking flex flex-col flex-1 h-full">
      <div className="EditBookingSubHeader flex items-center relative py-panel-padding">
        <div className="userCount flex items-center text-collins-primary pr-panel-padding">
          <User
            className="UserIcon h-4 w-4 mr-1 text-muted-foreground"
            size={16}
          />
          <span>{guestCount}</span>
        </div>
        <div className="BookingTime text-primary-color pr-panel-padding">
          {formatTimeForLocale(bookingTimeAsString, user?.locale)}
          {bookingTimeAsString !== bookingEndTimeAsString
            ? ` - ${formatTimeForLocale(bookingEndTimeAsString, user?.locale)}`
            : ""}
          <span className="BookingDuration ml-1 text-[#aab0bd]">
            {bookingDurationAsString && `(${bookingDurationAsString})`}
          </span>
        </div>
        {bookingType && (
          <EDSBadge
            variant="default"
            className="booking-type-badge"
            style={
              {
                "--booking-type-color": bookingType.color ?? "#e5e7eb",
                "--booking-type-foreground": "var(--black)"
              } as React.CSSProperties
            }
          >
            <span className="booking-type-badge-text">{bookingType.name}</span>
          </EDSBadge>
        )}
      </div>
      <div className="EditBookingScroll overflow-auto flex-1 min-h-0">
        <div className="BookingDetails">
          <div className="SubHeader text-collins-primary pt-panel-padding max-h-12">
            Booking Details
          </div>
          <div className="BookingDetailsContainer h-full">
            <EditBookingDetails
              booking={booking}
              updateBooking={(e) => {
                updateBooking(e);
              }}
            />
          </div>
        </div>
        {!booking.walkIn && (
          <div className="CustomerDetails pt-panel-padding">
            <div className="SubHeader text-collins-primary pt-panel-padding max-h-12">
              Customer Details
            </div>
            <div className="CustomerDetailsContainer h-full">
              <EditBookingCustomer
                booking={booking}
                updateBooking={(e) => {
                  updateBooking(e);
                }}
                disabled={!userCanManageBooking}
              />
            </div>
          </div>
        )}
      </div>
      <div className="EditBookingActionButtons flex items-center justify-between pt-panel-padding">
        <Button
          className="ActionButton w-[125px]"
          fill="outline"
          expand={true}
          type="default"
          onClick={() => handleClose()}
        >
          Cancel
        </Button>
        <Button
          disabled={saveDisabled}
          className="ActionButton w-[125px]"
          fill="solid"
          expand={true}
          type="primary"
          onClick={() => saveBooking()}
        >
          Save
        </Button>
      </div>
    </div>
  );

  return (
    <DetachedModal
      heading="Edit Booking"
      width="50rem"
      handleClose={handleClose}
    >
      {editBookingContent}
    </DetachedModal>
  );
};

export default React.memo<EditBookingType>(EditBooking);
